import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NavBar from "./NavBar";
import { markMessagesAsSeen } from "./useMessageCount";

const API_BASE_URL = "https://api.itrustsecure.com";
function getToken() { return localStorage.getItem("itrust_token"); }
function authHeaders() { return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }; }

export default function ChatPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [convo, setConvo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [showProposal, setShowProposal] = useState(false);
  const [proposalItem, setProposalItem] = useState(null);
  const [proposalPrice, setProposalPrice] = useState("");
  const [proposalQty, setProposalQty] = useState("1");
  const [submittingProposal, setSubmittingProposal] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/conversations/${id}`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) {
        setConvo(data);
        if (data.listing?.items?.length > 0 && !proposalItem) setProposalItem(data.listing.items[0]);
      }
    } catch (err) { console.warn(err); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => {
    load(); markMessagesAsSeen();
    pollRef.current = setInterval(load, 5000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [convo?.messages]);

  async function sendMessage(e) {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    try {
      await fetch(`${API_BASE_URL}/conversations/${id}/messages`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ body: message.trim() }) });
      setMessage(""); await load();
    } catch { window.alert("Could not send message"); }
    finally { setSending(false); }
  }

  async function submitProposal() {
    if (!proposalItem || !proposalPrice || Number(proposalPrice) <= 0) { window.alert("Please select an item and enter a valid price"); return; }
    setSubmittingProposal(true);
    try {
      const res = await fetch(`${API_BASE_URL}/conversations/${id}/propose-price`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ listingItemId: proposalItem.id, proposedPrice: Number(proposalPrice), quantity: Number(proposalQty) })
      });
      if (res.ok) { setShowProposal(false); setProposalPrice(""); await load(); }
      else { const d = await res.json(); window.alert(d.error || "Could not submit proposal"); }
    } catch { window.alert("Something went wrong"); }
    finally { setSubmittingProposal(false); }
  }

  async function acceptProposal(proposalId) {
    if (!window.confirm("Accept this price proposal?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/conversations/${id}/proposals/${proposalId}/accept`, { method: "PUT", headers: authHeaders() });
      if (res.ok) await load();
      else { const d = await res.json(); window.alert(d.error || "Could not accept"); }
    } catch { window.alert("Something went wrong"); }
  }

  async function declineProposal(proposalId) {
    try {
      const res = await fetch(`${API_BASE_URL}/conversations/${id}/proposals/${proposalId}/decline`, { method: "PUT", headers: authHeaders() });
      if (res.ok) await load();
      else { const d = await res.json(); window.alert(d.error || "Could not decline"); }
    } catch { window.alert("Something went wrong"); }
  }

  async function placeOrder(proposalId) {
    if (!window.confirm("Place order at the agreed price?")) return;
    setPlacingOrder(true);
    try {
      const res = await fetch(`${API_BASE_URL}/conversations/${id}/place-order`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ proposalId }) });
      const data = await res.json();
      if (res.ok) { window.alert("Order placed! Redirecting to checkout..."); navigate(`/checkout?orderId=${data.id}`); }
      else { window.alert(data.error || "Could not place order"); }
    } catch { window.alert("Something went wrong"); }
    finally { setPlacingOrder(false); }
  }

  const isBuyer = convo?.buyerId === user?.id;
  const acceptedProposals = convo?.priceProposals?.filter(p => p.status === "ACCEPTED") || [];
  const pendingProposals = convo?.priceProposals?.filter(p => p.status === "PENDING") || [];
  const other = convo ? (isBuyer ? convo.seller : convo.buyer) : null;

  const s = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "transparent", color: "#0f172a", minHeight: "100vh", display: "flex", flexDirection: "column" },
    chatWrap: { maxWidth: 760, margin: "0 auto", width: "100%", flex: 1, display: "flex", flexDirection: "column", padding: "0 20px 20px" },
    chatHeader: { padding: "20px 0 16px", borderBottom: "1px solid rgba(59,130,246,0.12)", marginBottom: 16 },
    backBtn: { background: "transparent", border: "none", color: "#64748b", cursor: "pointer", fontSize: 14, padding: 0, marginBottom: 10 },
    chatTitle: { fontSize: 22, fontWeight: 800, marginBottom: 4, color: "#0f172a" },
    chatSub: { fontSize: 13, color: "#64748b" },
    listingCard: { background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 12, padding: "12px 16px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" },
    listingCardTitle: { fontSize: 14, fontWeight: 700, color: "#1e40af" },
    listingCardSub: { fontSize: 12, color: "#64748b" },
    acceptedBanner: { background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.25)", borderRadius: 12, padding: 16, marginBottom: 16 },
    acceptedTitle: { fontSize: 14, fontWeight: 700, color: "#16a34a", marginBottom: 10 },
    acceptedItem: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
    placeOrderBtn: { background: "#16a34a", color: "#fff", border: "none", padding: "12px 20px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14, width: "100%", marginTop: 8 },
    messagesArea: { flex: 1, overflowY: "auto", marginBottom: 16, minHeight: 300, maxHeight: "calc(100vh - 400px)" },
    msgRow: (mine) => ({ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", marginBottom: 10 }),
    msgBubble: (mine, type) => ({
      maxWidth: "72%", padding: "10px 14px",
      borderRadius: mine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
      background: type === "PRICE_PROPOSAL" ? "rgba(217,119,6,0.1)" :
        type === "PRICE_ACCEPTED" ? "rgba(22,163,74,0.1)" :
        type === "PRICE_DECLINED" ? "rgba(220,38,38,0.08)" :
        type === "ORDER_PLACED" ? "rgba(124,58,237,0.1)" :
        mine ? "#3b82f6" : "rgba(255,255,255,0.85)",
      border: type === "PRICE_PROPOSAL" ? "1px solid rgba(217,119,6,0.25)" :
        type === "PRICE_ACCEPTED" ? "1px solid rgba(22,163,74,0.25)" :
        type === "ORDER_PLACED" ? "1px solid rgba(124,58,237,0.25)" :
        mine ? "none" : "1px solid rgba(59,130,246,0.12)",
      boxShadow: "0 2px 8px rgba(59,130,246,0.06)",
    }),
    msgText: { fontSize: 14, lineHeight: 1.5, color: (mine) => mine ? "#fff" : "#1e293b" },
    msgTime: { fontSize: 11, color: "rgba(100,116,139,0.8)", marginTop: 4 },
    msgSender: { fontSize: 11, color: "#64748b", marginBottom: 4 },
    pendingProposalCard: { background: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.25)", borderRadius: 12, padding: 14, marginBottom: 12 },
    proposalTitle: { fontSize: 13, fontWeight: 700, color: "#d97706", marginBottom: 8 },
    proposalRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
    proposalBtns: { display: "flex", gap: 8 },
    acceptBtn: { background: "#16a34a", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13 },
    declineBtn: { background: "rgba(220,38,38,0.08)", color: "#dc2626", border: "1px solid rgba(220,38,38,0.25)", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13 },
    inputRow: { display: "flex", gap: 10, alignItems: "flex-end" },
    input: { flex: 1, padding: "12px 16px", background: "rgba(255,255,255,0.8)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 12, color: "#0f172a", fontSize: 14, outline: "none", resize: "none", boxShadow: "0 2px 8px rgba(59,130,246,0.06)" },
    sendBtn: { background: "#3b82f6", color: "#fff", border: "none", padding: "12px 20px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 14, boxShadow: "0 4px 12px rgba(59,130,246,0.3)" },
    proposeBtn: { background: "rgba(217,119,6,0.08)", color: "#d97706", border: "1px solid rgba(217,119,6,0.25)", padding: "12px 16px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 },
    modal: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20, backdropFilter: "blur(4px)" },
    modalCard: { background: "#fff", border: "1px solid rgba(59,130,246,0.15)", borderRadius: 20, padding: 28, width: "100%", maxWidth: 460, boxShadow: "0 20px 60px rgba(59,130,246,0.15)" },
    modalTitle: { fontSize: 20, fontWeight: 800, marginBottom: 6, color: "#0f172a" },
    modalSub: { fontSize: 13, color: "#64748b", marginBottom: 20 },
    label: { fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 6, display: "block" },
    modalInput: { width: "100%", padding: "12px 14px", background: "rgba(59,130,246,0.04)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, color: "#0f172a", fontSize: 15, marginBottom: 14, boxSizing: "border-box", outline: "none" },
    modalSelect: { width: "100%", padding: "12px 14px", background: "#fff", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, color: "#0f172a", fontSize: 14, marginBottom: 14, boxSizing: "border-box", outline: "none" },
    modalActions: { display: "flex", gap: 10, marginTop: 4 },
    modalCancel: { flex: 1, background: "transparent", border: "1px solid rgba(59,130,246,0.2)", color: "#64748b", padding: "12px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14 },
    modalSubmit: { flex: 2, background: "#d97706", color: "#fff", border: "none", padding: "12px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14 },
  };

  if (loading) return (
    <div style={s.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh", color: "#94a3b8" }}>Loading...</div>
    </div>
  );

  if (!convo) return (
    <div style={s.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh", color: "#94a3b8" }}>Conversation not found</div>
    </div>
  );

  return (
    <div style={s.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={s.chatWrap}>
        <div style={s.chatHeader}>
          <button style={s.backBtn} onClick={() => navigate("/messages")}>← Back to Messages</button>
          <div style={s.chatTitle}>{other?.fullName}</div>
          {convo.listing && <div style={s.chatSub}>Re: {convo.listing.title}</div>}
        </div>

        {convo.listing && (
          <div style={s.listingCard} onClick={() => navigate(`/listing-detail/${convo.listingId}`)}>
            <div>
              <div style={s.listingCardTitle}>🛍️ {convo.listing.title}</div>
              <div style={s.listingCardSub}>{convo.listing.items?.length} items available · Click to view</div>
            </div>
            <span style={{ color: "#94a3b8", fontSize: 18 }}>→</span>
          </div>
        )}

        {isBuyer && acceptedProposals.length > 0 && (
          <div style={s.acceptedBanner}>
            <div style={s.acceptedTitle}>✅ Agreed Prices — Ready to Order</div>
            {acceptedProposals.map(p => (
              <div key={p.id}>
                <div style={s.acceptedItem}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>{p.listingItem?.title}</div>
                    <div style={{ fontSize: 13, color: "#64748b" }}>
                      {p.quantity}x at <span style={{ color: "#16a34a", fontWeight: 700 }}>${Number(p.proposedPrice).toFixed(2)}</span> each
                      <span style={{ color: "#94a3b8" }}> (was ${Number(p.listingItem?.listedUnitPrice).toFixed(2)})</span>
                    </div>
                  </div>
                </div>
                <button style={{ ...s.placeOrderBtn, opacity: placingOrder ? 0.5 : 1 }} onClick={() => placeOrder(p.id)} disabled={placingOrder}>
                  {placingOrder ? "Placing Order..." : `🛒 Place Order at $${(Number(p.proposedPrice) * p.quantity * 1.025).toFixed(2)}`}
                </button>
              </div>
            ))}
          </div>
        )}

        {pendingProposals.length > 0 && (
          <div style={s.pendingProposalCard}>
            <div style={s.proposalTitle}>💰 Pending Price Proposal</div>
            {pendingProposals.map(p => (
              <div key={p.id}>
                <div style={s.proposalRow}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>{p.listingItem?.title}</div>
                    <div style={{ fontSize: 13, color: "#64748b" }}>
                      {p.quantity}x · Proposed: <span style={{ color: "#d97706", fontWeight: 700 }}>${Number(p.proposedPrice).toFixed(2)}</span>
                      <span style={{ color: "#94a3b8" }}> (listed: ${Number(p.listingItem?.listedUnitPrice).toFixed(2)})</span>
                    </div>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>by {p.proposedBy?.fullName}</div>
                  </div>
                </div>
                {p.proposedById !== user?.id && (
                  <div style={s.proposalBtns}>
                    <button style={s.acceptBtn} onClick={() => acceptProposal(p.id)}>✓ Accept</button>
                    <button style={s.declineBtn} onClick={() => declineProposal(p.id)}>✕ Decline</button>
                    <button style={{ ...s.declineBtn, color: "#d97706", borderColor: "rgba(217,119,6,0.3)" }}
                      onClick={() => { setShowProposal(true); setProposalItem(convo.listing?.items?.find(i => i.id === p.listingItemId) || proposalItem); setProposalPrice(String(Number(p.proposedPrice))); }}>
                      Counter
                    </button>
                  </div>
                )}
                {p.proposedById === user?.id && <div style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>Waiting for response...</div>}
              </div>
            ))}
          </div>
        )}

        <div style={s.messagesArea}>
          {convo.messages?.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "#94a3b8", fontSize: 14 }}>No messages yet. Say hello!</div>
          ) : convo.messages?.map(msg => {
            const mine = msg.senderId === user?.id;
            return (
              <div key={msg.id} style={s.msgRow(mine)}>
                <div style={s.msgBubble(mine, msg.messageType)}>
                  {!mine && <div style={s.msgSender}>{msg.sender?.fullName}</div>}
                  <div style={{ ...s.msgText, color: mine ? "#fff" : "#1e293b" }}>{msg.body}</div>
                  <div style={s.msgTime}>{new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={sendMessage} style={s.inputRow}>
          <textarea style={s.input} placeholder="Type a message..." value={message} onChange={e => setMessage(e.target.value)} rows={1}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(e); } }} />
          {convo.listing && <button type="button" style={s.proposeBtn} onClick={() => setShowProposal(true)}>💰 Propose Price</button>}
          <button type="submit" style={s.sendBtn} disabled={sending}>{sending ? "..." : "Send"}</button>
        </form>
      </div>

      {showProposal && (
        <div style={s.modal} onClick={() => setShowProposal(false)}>
          <div style={s.modalCard} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>💰 Propose a Price</div>
            <div style={s.modalSub}>Suggest a negotiated price for an item</div>
            <label style={s.label}>Item</label>
            <select style={s.modalSelect} value={proposalItem?.id || ""} onChange={e => setProposalItem(convo.listing?.items?.find(i => i.id === e.target.value))}>
              {convo.listing?.items?.map(item => (
                <option key={item.id} value={item.id}>{item.title} — Listed at ${Number(item.listedUnitPrice).toFixed(2)}</option>
              ))}
            </select>
            <label style={s.label}>Quantity</label>
            <input style={s.modalInput} type="number" min="1" max={proposalItem?.availableQuantity || 1} value={proposalQty} onChange={e => setProposalQty(e.target.value)} />
            <label style={s.label}>Your Proposed Price (per item)</label>
            <input style={s.modalInput} type="number" step="0.01" min="0.01" placeholder={`Listed: $${Number(proposalItem?.listedUnitPrice || 0).toFixed(2)}`} value={proposalPrice} onChange={e => setProposalPrice(e.target.value)} />
            {proposalPrice && (
              <div style={{ background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.2)", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#1e293b" }}>
                Total: <strong style={{ color: "#d97706" }}>${(Number(proposalPrice) * Number(proposalQty) * 1.025).toFixed(2)}</strong>
                <span style={{ color: "#94a3b8" }}> (incl. 2.5% platform fee)</span>
              </div>
            )}
            <div style={s.modalActions}>
              <button style={s.modalCancel} onClick={() => setShowProposal(false)}>Cancel</button>
              <button style={{ ...s.modalSubmit, opacity: submittingProposal ? 0.5 : 1 }} onClick={submitProposal} disabled={submittingProposal}>
                {submittingProposal ? "Sending..." : "Send Proposal"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}